import { useMemo, useState } from 'react';
import { usePageTitle } from '../../../hooks/usePageTitle';
import { STRIPE_LINKS } from '../../../lib/stripe';
import CalculatorLayout from '../../../components/calculator/CalculatorLayout';
import NumberInput from '../../../components/calculator/NumberInput';
import PercentInput from '../../../components/calculator/PercentInput';
import BigResult from '../../../components/calculator/BigResult';
import EmailCapture from '../../../components/calculator/EmailCapture';
import CtaBlock from '../../../components/calculator/CtaBlock';
import FaqSection from '../../../components/calculator/FaqSection';
import RelatedCalculators from '../../../components/calculator/RelatedCalculators';

const SLUG = 'review-generation-roi';
const CANONICAL = `https://floridaaimarketingexperts.com/calculator/${SLUG}`;

const LIFT_PER_HALF_STAR = 0.09;

export default function ReviewGenerationRoiCalculatorPage() {
  const softwareSchema = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'Google Review Generation ROI Calculator',
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Any (Web)',
    url: CANONICAL,
    offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
    description:
      'Free Google review ROI calculator. See the additional revenue you can generate by lifting your star rating half a point.',
  };
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'How much does a 0.5 star rating increase affect revenue?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Industry benchmarks from BrightLocal and Harvard Business Review show a 0.5-star increase produces roughly a 9% lift in revenue for local service businesses. The lift is driven by higher click-through from map listings and higher close rate on calls.',
        },
      },
      {
        '@type': 'Question',
        name: 'What star rating do I need to compete?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'For local businesses in Florida, 4.7-4.9 stars is the competitive sweet spot. Below 4.5 you lose clicks to better-rated competitors. Above 4.9 requires tight review velocity management.',
        },
      },
      {
        '@type': 'Question',
        name: 'How do I get more Google reviews?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Ask every customer immediately after the job, via SMS with a direct Google review link. Most businesses that implement consistent review requests lift from 30% review velocity to 70%+ within 90 days.',
        },
      },
    ],
  };

  usePageTitle({
    title: 'Google Review Generation ROI Calculator | FAME',
    description:
      'Free Google review ROI calculator. See how much additional revenue you can generate by raising your star rating half a point.',
    keywords:
      'google review ROI calculator, review generation calculator, star rating revenue calculator, business reviews roi',
    canonical: CANONICAL,
    schema: [softwareSchema, faqSchema],
  });

  const [currentRating, setCurrentRating] = useState(4.3);
  const [monthlyLeads, setMonthlyLeads] = useState(120);
  const [closeRate, setCloseRate] = useState(35);
  const [avgJobValue, setAvgJobValue] = useState(2500);

  const results = useMemo(() => {
    const monthlyJobs = monthlyLeads * (closeRate / 100);
    const currentMonthlyRevenue = monthlyJobs * avgJobValue;
    const monthlyLift = currentMonthlyRevenue * LIFT_PER_HALF_STAR;
    const annualLift = monthlyLift * 12;
    const targetRating = Math.min(5, currentRating + 0.5);
    return { currentMonthlyRevenue, monthlyLift, annualLift, targetRating };
  }, [currentRating, monthlyLeads, closeRate, avgJobValue]);

  const faqs = [
    {
      question: 'Why does the 9% lift per half-star benchmark hold up?',
      answer:
        'It is consistent across studies by BrightLocal, Harvard Business Review, Womply, and ReviewTrackers. The mechanism is simple: higher-rated businesses get more map-pack clicks, higher call conversion, and more word-of-mouth. It compounds.',
    },
    {
      question: 'What if I have bad reviews I cannot remove?',
      answer:
        'The fastest way to dilute bad reviews is velocity. Replacing a 3.8 average with a 4.6 is usually possible in 60-120 days by collecting 30-60 new 5-star reviews. The bad ones do not disappear — they get buried under the new volume.',
    },
    {
      question: 'Do Google reviews help my SEO rankings?',
      answer:
        'Yes — review count, review velocity, and review quality are all ranking signals for local pack results. A business with 200 reviews at 4.8 stars outranks one with 40 reviews at 4.9 stars almost every time.',
    },
  ];

  return (
    <CalculatorLayout>
      <section className="relative overflow-hidden bg-white grid-bg py-16 md:py-20">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <span className="eyebrow mb-5">
            <i className="ri-star-smile-line mr-2"></i>Free Calculator · Reviews Drive Revenue
          </span>
          <h1 className="h-display text-4xl md:text-6xl mb-5 leading-[0.95] tracking-[-0.02em]">
            Google Review Generation ROI Calculator
          </h1>
          <p className="text-lg md:text-xl text-slate-600 font-body font-light max-w-3xl mx-auto">
            See the additional revenue you can generate by lifting your star rating half a point.
            Industry benchmark: 9% revenue increase per 0.5 stars.
          </p>
        </div>
      </section>

      <section className="py-12 md:py-16">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            <div className="fame-card p-8 shadow-xl">
              <h2 className="text-navy-900 text-2xl font-display font-bold mb-6">Your Numbers</h2>
              <div className="flex flex-col gap-6">
                <NumberInput
                  label="Current Google star rating"
                  value={currentRating}
                  onChange={setCurrentRating}
                  min={1}
                  max={5}
                  step={0.1}
                  suffix="★"
                  helperText="Your current Google Business Profile rating."
                />
                <NumberInput
                  label="Monthly leads"
                  value={monthlyLeads}
                  onChange={setMonthlyLeads}
                  step={5}
                  helperText="All inbound leads: calls, forms, map clicks."
                />
                <PercentInput
                  label="Close rate"
                  value={closeRate}
                  onChange={setCloseRate}
                />
                <NumberInput
                  label="Average deal value"
                  value={avgJobValue}
                  onChange={setAvgJobValue}
                  prefix="$"
                  step={100}
                />
              </div>
            </div>

            <div className="flex flex-col gap-5">
              <BigResult
                label={`Additional Monthly Revenue at ${results.targetRating.toFixed(1)}★`}
                value={results.monthlyLift}
                format="currency"
                accent="green"
                sublabel="Based on 9% lift from a half-star increase."
              />
              <BigResult
                label="Additional Annual Revenue"
                value={results.annualLift}
                format="currency"
                accent="orange"
                size="md"
              />
              <BigResult
                label="Current Monthly Revenue"
                value={results.currentMonthlyRevenue}
                format="currency"
                accent="teal"
                size="md"
                sublabel="At your current rating and close rate."
              />

              <CtaBlock
                variant="compact"
                eyebrow="Reviews + reputation, installed"
                headline="FASS Standard · $1,997/mo · or Starter Retainer"
                subheadline="Review requests, replies, reputation monitoring, map-pack SEO — end-to-end."
                primaryLabel="Start FASS"
                primaryHref={STRIPE_LINKS.T8_FASS_STANDARD}
              />
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 bg-navy-50">
        <div className="max-w-3xl mx-auto px-6">
          <h2 className="text-navy-900 text-2xl md:text-3xl font-display font-bold mb-4">
            Why a half-star move on Google is worth more than most marketing campaigns
          </h2>
          <p className="text-navy-600 text-base md:text-lg leading-relaxed mb-4">
            Google rewards higher-rated businesses with more map-pack impressions, higher
            click-through, and higher conversion rates at every stage of the funnel. BrightLocal,
            Harvard Business Review, and Womply studies converge on roughly a{' '}
            <strong>9% revenue increase per 0.5 star rating improvement</strong> for local service
            businesses. That benchmark drives this calculator.
          </p>
          <p className="text-navy-600 text-base md:text-lg leading-relaxed mb-4">
            For a Florida business doing $100K/month, moving from 4.3 to 4.8 stars is worth{' '}
            <strong>about $9,000 a month in recovered revenue</strong>. That is usually more than
            any paid ad campaign, and it is a one-time systems fix, not a recurring spend. The
            biggest lever is not &quot;ask for reviews&quot; — it is building an automated request
            workflow that hits every customer within 10 minutes of delivery, when satisfaction is
            highest.
          </p>
          <p className="text-navy-600 text-base md:text-lg leading-relaxed">
            Most businesses also leave 70%+ of their reviews unreplied. Replying to every review
            (good and bad) signals Google the business is active and boosts map-pack visibility
            further. FAME FASS Standard handles the whole workflow end-to-end.
          </p>
        </div>
      </section>

      <FaqSection items={faqs} />

      <CtaBlock
        eyebrow="Turn reviews into revenue"
        headline="Install the review + reputation engine"
        subheadline="FASS Standard handles review requests, replies, reputation monitoring, and map-pack SEO. $1,997/mo. Or roll into a T10 Starter Retainer."
        primaryLabel="FASS Standard · $1,997/mo"
        primaryHref={STRIPE_LINKS.T8_FASS_STANDARD}
        secondaryLabel="Starter Retainer · $2,500/mo"
        secondaryHref={STRIPE_LINKS.T10_STARTER}
      />

      <section className="py-12 bg-white">
        <div className="max-w-3xl mx-auto px-6">
          <EmailCapture
            slug={SLUG}
            calculatorInputs={{ currentRating, monthlyLeads, closeRate, avgJobValue }}
            calculatorResults={{
              monthlyLift: Math.round(results.monthlyLift),
              annualLift: Math.round(results.annualLift),
              currentMonthlyRevenue: Math.round(results.currentMonthlyRevenue),
              targetRating: results.targetRating,
            }}
          />
        </div>
      </section>

      <RelatedCalculators excludeSlug={SLUG} />
    </CalculatorLayout>
  );
}
